// Vehicles are saved to the logged-in customer's account by the KOTS My Garage app,
// which the shop exposes at /apps/garage.
const GarageApi = {
  async request(url, body) {
    const options = body
      ? {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body),
        }
      : { headers: { Accept: 'application/json' } };
    const response = await fetch(url, options);

    let result = null;
    try {
      result = await response.json();
    } catch (error) {
      // Not JSON: handled below as a failed request.
    }
    if (!response.ok || !result || !Array.isArray(result.vehicles)) {
      throw new Error((result && result.message) || '');
    }
    return result;
  },

  title(vehicle) {
    return [vehicle.year, vehicle.make, vehicle.model].filter(Boolean).join(' ');
  },

  // "6 cyl", or the word as saved (Rotary).
  cylinders(vehicle) {
    if (!vehicle.cylinders) return '';
    return /^\d+$/.test(vehicle.cylinders) ? `${vehicle.cylinders} cyl` : vehicle.cylinders;
  },

  // Which part of a photo shows in a cropped frame: "x y" percentages saved with the photo.
  position(saved) {
    const [x, y] = String(saved || '')
      .split(' ')
      .map(Number);
    return Number.isFinite(x) && Number.isFinite(y) ? { x, y } : { x: 50, y: 50 };
  },
};

if (!customElements.get('my-garage')) {
  customElements.define(
    'my-garage',
    class MyGarage extends HTMLElement {
      connectedCallback() {
        this.list = this.querySelector('[data-garage-list]');
        // Logged-out customers get a log-in prompt from Liquid instead of the garage.
        if (!this.list) return;

        this.api = this.dataset.api;
        this.vehicles = [];
        this.status = this.querySelector('[data-garage-status]');
        this.addCard = this.querySelector('[data-garage-add-card]');
        this.addButton = this.querySelector('[data-garage-add]');
        this.dialog = this.querySelector('dialog');
        this.form = this.querySelector('form');
        this.error = this.querySelector('[data-garage-error]');
        this.saveButton = this.querySelector('[data-garage-save]');
        this.removeButton = this.querySelector('[data-garage-remove]');
        this.photoArea = this.querySelector('[data-garage-photos]');
        this.photoPreview = this.querySelector('[data-garage-photo-preview]');
        this.photoFrame = this.querySelector('[data-garage-photo-frame]');
        this.photoHint = this.querySelector('[data-garage-photo-hint]');
        this.photoList = this.querySelector('[data-garage-thumbs]');
        this.photoAdd = this.querySelector('[data-garage-photo-add]');
        this.photoOrderHint = this.querySelector('[data-garage-photo-order-hint]');
        this.photoCover = this.querySelector('[data-garage-photo-cover]');
        this.photoClear = this.querySelector('[data-garage-photo-clear]');
        this.maxPhotos = Number(this.photoList.dataset.max);
        this.dialogTitle = this.querySelector('[data-garage-dialog-title]');
        this.viewer = this.querySelector('[data-garage-viewer]');
        this.viewerPhotos = this.querySelector('[data-garage-viewer-photos]');
        this.viewerControls = ['count', 'previous', 'next'].map((name) =>
          this.querySelector(`[data-garage-viewer-${name}]`)
        );
        this.viewerSlides = this.slides(this.viewerPhotos, ...this.viewerControls);

        this.addCard.querySelector('button').addEventListener('click', () => this.open());
        this.addButton.addEventListener('click', () => this.open());
        this.querySelector('[data-garage-retry]').addEventListener('click', () => this.load());
        this.querySelector('[data-garage-cancel]').addEventListener('click', () => this.dialog.close());
        this.querySelector('[data-garage-close]').addEventListener('click', () => this.dialog.close());
        this.form.addEventListener('submit', this.onSubmit.bind(this));
        this.form.elements.photo.addEventListener('change', this.onPhotoChange.bind(this));
        this.photoList.addEventListener('click', this.onThumbClick.bind(this));
        this.photoList.addEventListener('pointerdown', this.onThumbDragStart.bind(this));
        this.photoList.addEventListener('pointermove', this.onThumbDrag.bind(this));
        this.photoList.addEventListener('pointerup', this.onThumbDragEnd.bind(this));
        this.photoList.addEventListener('pointercancel', this.onThumbDragEnd.bind(this));
        this.photoCover.addEventListener('click', this.onPhotoCover.bind(this));
        this.photoClear.addEventListener('click', this.onPhotoClear.bind(this));
        this.photoFrame.addEventListener('pointerdown', this.onPhotoDragStart.bind(this));
        this.photoFrame.addEventListener('pointermove', this.onPhotoDrag.bind(this));
        this.photoFrame.addEventListener('pointerup', this.onPhotoDragEnd.bind(this));
        this.photoFrame.addEventListener('pointercancel', this.onPhotoDragEnd.bind(this));
        this.photoFrame.addEventListener('keydown', this.onPhotoKey.bind(this));
        this.removeButton.addEventListener('click', this.onRemove.bind(this));
        this.viewer.addEventListener('click', this.onViewerClick.bind(this));
        this.viewer.addEventListener('keydown', this.onViewerKey.bind(this));
        this.querySelector('[data-garage-viewer-close]').addEventListener('click', () => this.viewer.close());

        this.load();
      }

      async load() {
        this.setStatus('loading');
        try {
          this.vehicles = (await GarageApi.request(`${this.api}/vehicles`)).vehicles;
          this.setStatus('ready');
          this.render();
        } catch (error) {
          this.setStatus('failed');
        }
      }

      setStatus(state) {
        this.status.hidden = state === 'ready';
        this.list.hidden = state !== 'ready';
        this.addButton.disabled = state !== 'ready';
        this.status.querySelector('[data-garage-loading]').hidden = state !== 'loading';
        this.status.querySelector('[data-garage-failed]').hidden = state !== 'failed';
      }

      render() {
        this.list.querySelectorAll('[data-garage-vehicle]').forEach((card) => card.remove());
        this.vehicles.forEach((vehicle) => this.list.insertBefore(this.card(vehicle), this.addCard));
        this.setStat('vehicles', this.vehicles.length);
        this.setStat(
          'photos',
          this.vehicles.reduce((count, vehicle) => count + vehicle.photos.length, 0)
        );
      }

      setStat(name, count) {
        const stat = this.querySelector(`[data-garage-stat="${name}"]`);
        const label = stat.querySelector('dt');
        stat.querySelector('dd').textContent = count;
        label.textContent = count === 1 ? label.dataset.one : label.dataset.other;
      }

      card(vehicle) {
        const item = this.querySelector('[data-garage-card-template]').content.firstElementChild.cloneNode(true);
        const title = vehicle.nickname || GarageApi.title(vehicle);
        // A photo Shopify is still processing has no address yet, so it is left out.
        this.cardPhotos(
          item,
          title,
          vehicle.photos.filter((photo) => photo.url)
        );

        item.querySelector('[data-garage-card-title]').textContent = title;
        const notes = item.querySelector('[data-garage-card-notes]');
        notes.textContent = vehicle.notes || notes.dataset.empty;
        notes.classList.toggle('my-garage__card-notes--empty', !vehicle.notes);

        const tags = item.querySelector('[data-garage-card-tags]');
        const addTag = (value) => {
          const tag = document.createElement('li');
          tag.textContent = value;
          return tags.appendChild(tag);
        };
        [
          vehicle.year, vehicle.make, vehicle.model, vehicle.colour, vehicle.rego,
          vehicle.drivetrain, vehicle.transmission, vehicle.engineCode, vehicle.induction, vehicle.fuel,
          GarageApi.cylinders(vehicle), vehicle.power,
        ]
          .filter(Boolean)
          .forEach(addTag);
        if (vehicle.speedClass) {
          addTag(`${tags.dataset.classLabel}: ${vehicle.speedClass}`).classList.add('my-garage__card-class');
        }

        item.querySelector('[data-garage-card]').addEventListener('click', () => this.open(vehicle));
        return item;
      }

      // Puts the photos side by side in the card's frame, cover first. The frame scrolls sideways one
      // photo at a time, so a touch screen swipes through them; the arrows do the same for a mouse or keyboard.
      cardPhotos(item, title, photos) {
        if (photos.length === 0) return;

        const frame = item.querySelector('[data-garage-card-photos]');
        photos.forEach((photo, index) => {
          const image = this.photoImage(title, photos, index);
          const position = GarageApi.position(photo.position);
          image.width = 800;
          image.height = 500;
          image.style.objectPosition = `${position.x}% ${position.y}%`;
          frame.appendChild(image);
        });
        frame.hidden = false;
        // An SVG has no `hidden` property, so set the attribute.
        item.querySelector('[data-garage-card-placeholder]').setAttribute('hidden', '');

        let shown = () => 0;
        if (photos.length > 1) {
          const controls = ['count', 'previous', 'next'].map((name) => item.querySelector(`[data-garage-card-${name}]`));
          controls.forEach((control) => {
            control.hidden = false;
          });
          shown = this.slides(frame, ...controls).shown;
        }

        // A click on a photo shows it full size rather than opening the vehicle, as does Enter from the keyboard.
        const view = (event) => {
          event.stopPropagation();
          this.view(title, photos, shown());
        };
        frame.addEventListener('click', view);
        frame.addEventListener('keydown', (event) => {
          if (event.key === 'Enter') view(event);
        });
      }

      photoImage(title, photos, index) {
        const image = document.createElement('img');
        image.loading = 'lazy';
        image.src = photos[index].url;
        image.alt = photos.length > 1 ? `${title} (${index + 1}/${photos.length})` : title;
        return image;
      }

      // Runs a row of photos that scrolls sideways one at a time: the count follows it, and the arrows
      // move it, carrying on round past either end.
      slides(frame, count, previous, next) {
        // The frame has no width until it is on the page, when the first photo is the one showing.
        const shown = () => Math.round(frame.scrollLeft / frame.clientWidth) || 0;
        const refresh = () => {
          count.textContent = count.dataset.label
            .replace('[number]', shown() + 1)
            .replace('[count]', frame.children.length);
        };
        const step = (by) => (event) => {
          event.stopPropagation();
          const total = frame.children.length;
          frame.scrollTo({ left: ((shown() + by + total) % total) * frame.clientWidth });
        };

        frame.addEventListener('scroll', refresh, { passive: true });
        previous.addEventListener('click', step(-1));
        next.addEventListener('click', step(1));
        refresh();
        return { shown, refresh };
      }

      // Shows a vehicle's photos full size, starting from the one its card was showing.
      view(title, photos, index) {
        this.viewerPhotos.replaceChildren(
          ...photos.map((photo, position) => {
            const slide = document.createElement('div');
            slide.appendChild(this.photoImage(title, photos, position));
            return slide;
          })
        );
        this.viewerControls.forEach((control) => {
          control.hidden = photos.length < 2;
        });
        this.viewer.showModal();
        this.viewerPhotos.scrollTo({ left: index * this.viewerPhotos.clientWidth, behavior: 'instant' });
        this.viewerSlides.refresh();
      }

      // Anywhere that is not a photo or a button closes the viewer.
      onViewerClick(event) {
        if (!event.target.closest('img, button')) this.viewer.close();
      }

      onViewerKey(event) {
        const arrow = { ArrowLeft: 1, ArrowRight: 2 }[event.key];
        if (!arrow) return;

        // Otherwise the browser would also scroll the row a little by itself.
        event.preventDefault();
        this.viewerControls[arrow].click();
      }

      open(vehicle) {
        // One vehicle at a time: a save still in progress has to finish first.
        if (this.busy) return;

        this.form.reset();
        this.editing = vehicle || null;
        this.error.hidden = true;
        this.removeButton.hidden = !vehicle;
        this.resetRemove();
        this.dialogTitle.textContent = vehicle ? this.dialogTitle.dataset.edit : this.dialogTitle.dataset.add;

        if (vehicle) {
          ['nickname', 'year', 'make', 'model', 'colour', 'rego', 'speedClass', 'drivetrain', 'transmission', 'induction', 'fuel', 'cylinders', 'engineCode', 'power', 'notes'].forEach((field) => {
            this.form.elements[field].value = vehicle[field] || '';
          });
        }

        // Photos the garage already has carry an id; ones added here carry none until they are uploaded.
        this.photos = (vehicle ? vehicle.photos : []).map((photo) => ({
          id: photo.id,
          src: photo.url,
          position: GarageApi.position(photo.position),
        }));
        this.renderPhotos(0);
        this.dialog.showModal();
      }

      // Redraws the row of thumbnails, then shows one of the photos in the frame.
      renderPhotos(selected) {
        this.photoList.querySelectorAll('[data-garage-thumb]').forEach((thumb) => thumb.remove());
        this.photos.forEach((photo, index) => this.photoList.insertBefore(this.thumb(photo, index), this.photoAdd));
        this.photoAdd.hidden = this.photos.length >= this.maxPhotos;
        this.photoOrderHint.hidden = this.photos.length < 2;
        this.selectPhoto(selected);
      }

      thumbs() {
        return [...this.photoList.querySelectorAll('[data-garage-thumb]')];
      }

      thumb(photo, index) {
        const item = this.querySelector('[data-garage-thumb-template]').content.firstElementChild.cloneNode(true);
        const image = item.querySelector('img');
        const label = item.querySelector('[data-garage-thumb-label]');

        // A photo Shopify is still processing has no address yet.
        if (photo.src) image.src = photo.src;
        else image.hidden = true;
        image.style.objectPosition = `${photo.position.x}% ${photo.position.y}%`;
        label.textContent = `${label.dataset.label} ${index + 1}`;
        item.querySelector('[data-garage-thumb-cover]').hidden = index !== 0;
        return item;
      }

      onThumbClick(event) {
        const index = this.thumbs().indexOf(event.target.closest('[data-garage-thumb]'));
        if (index !== -1) this.selectPhoto(index);
      }

      // A thumbnail is dragged over the others to change the photos' order. Capturing the pointer keeps
      // the drag going when it strays outside the row.
      onThumbDragStart(event) {
        const thumb = event.target.closest('[data-garage-thumb]');
        if (!thumb || event.button !== 0) return;

        this.reorder = { thumb, pointer: event.pointerId, x: event.clientX, y: event.clientY, order: null };
        thumb.setPointerCapture(event.pointerId);
      }

      onThumbDrag(event) {
        const drag = this.reorder;
        if (!drag || drag.pointer !== event.pointerId) return;

        if (!drag.order) {
          // Nothing moves until the pointer has travelled a little, so a plain click still only selects.
          if (Math.hypot(event.clientX - drag.x, event.clientY - drag.y) < 6) return;
          drag.order = this.thumbs();
          drag.thumb.querySelector('button').classList.add('my-garage__thumb--dragging');
          this.selectPhoto(drag.order.indexOf(drag.thumb));
        }

        const over = drag.order.find((thumb) => {
          const box = thumb.getBoundingClientRect();
          return (
            event.clientX >= box.left && event.clientX <= box.right && event.clientY >= box.top && event.clientY <= box.bottom
          );
        });
        if (!over || over === drag.thumb) return;

        // The dragged thumbnail takes the place of the one under the pointer.
        const place = drag.order.indexOf(over);
        drag.order.splice(drag.order.indexOf(drag.thumb), 1);
        drag.order.splice(place, 0, drag.thumb);
        drag.order.forEach((thumb, index) => {
          // Shown in the new order without being moved in the page, which would end the drag. The
          // numbers are negative so the add tile, at zero, stays last.
          thumb.style.order = index - drag.order.length;
          thumb.querySelector('[data-garage-thumb-cover]').hidden = index !== 0;
        });
      }

      onThumbDragEnd(event) {
        const drag = this.reorder;
        if (!drag || drag.pointer !== event.pointerId) return;

        this.reorder = null;
        // Not dragged far enough to count, or the thumbnails were redrawn mid-drag when a new photo arrived.
        if (!drag.order || !drag.thumb.isConnected) return;

        const before = this.thumbs();
        // A drag the browser called off leaves the photos as they were.
        const order = event.type === 'pointerup' ? drag.order : before;
        this.photos.splice(0, this.photos.length, ...order.map((thumb) => this.photos[before.indexOf(thumb)]));
        this.renderPhotos(order.indexOf(drag.thumb));
      }

      // The selected photo shows in the frame, where dragging sets its position.
      selectPhoto(index) {
        this.selected = Math.max(0, Math.min(index, this.photos.length - 1));
        const photo = this.photos[this.selected];

        this.photoFrame.hidden = !photo;
        this.photoHint.hidden = !photo;
        this.photoClear.hidden = !photo;
        this.photoCover.hidden = !photo || this.selected === 0;
        this.photoList.querySelectorAll('[data-garage-thumb] button').forEach((button, position) => {
          button.setAttribute('aria-pressed', position === this.selected);
        });

        const source = (photo && photo.src) || null;
        // Left alone when it is already showing, so the photo does not blink while its thumbnail is dragged.
        if (!source) this.photoPreview.removeAttribute('src');
        else if (this.photoPreview.getAttribute('src') !== source) this.photoPreview.src = source;
        if (photo) this.setPosition(photo.position.x, photo.position.y);
      }

      // Moves the selected photo, in the frame and in its thumbnail.
      setPosition(x, y) {
        const clamp = (value) => Math.min(100, Math.max(0, Math.round(value)));
        const position = { x: clamp(x), y: clamp(y) };
        const thumb = this.photoList.querySelectorAll('[data-garage-thumb] img')[this.selected];

        this.photos[this.selected].position = position;
        this.photoPreview.style.objectPosition = `${position.x}% ${position.y}%`;
        thumb.style.objectPosition = `${position.x}% ${position.y}%`;
      }

      // How far the photo overhangs the frame on each axis once it is scaled to cover it.
      photoOverhang() {
        const frame = this.photoFrame.getBoundingClientRect();
        const { naturalWidth, naturalHeight } = this.photoPreview;
        if (!naturalWidth || !naturalHeight) return { x: 0, y: 0 };

        const scale = Math.max(frame.width / naturalWidth, frame.height / naturalHeight);
        return { x: naturalWidth * scale - frame.width, y: naturalHeight * scale - frame.height };
      }

      onPhotoDragStart(event) {
        this.photoFrame.setPointerCapture(event.pointerId);
        this.photoFrame.classList.add('my-garage__photo-frame--dragging');
        this.drag = {
          x: event.clientX,
          y: event.clientY,
          start: this.photos[this.selected].position,
          overhang: this.photoOverhang(),
        };
      }

      // Dragging the photo right reveals more of its left side, so the position moves the other way.
      onPhotoDrag(event) {
        if (!this.drag) return;
        const { start, overhang } = this.drag;
        const moved = (distance, room) => (room >= 1 ? (distance / room) * 100 : 0);
        this.setPosition(
          start.x - moved(event.clientX - this.drag.x, overhang.x),
          start.y - moved(event.clientY - this.drag.y, overhang.y)
        );
      }

      onPhotoDragEnd() {
        this.drag = null;
        this.photoFrame.classList.remove('my-garage__photo-frame--dragging');
      }

      onPhotoKey(event) {
        const step = { ArrowLeft: [-5, 0], ArrowRight: [5, 0], ArrowUp: [0, -5], ArrowDown: [0, 5] }[event.key];
        if (!step) return;

        event.preventDefault();
        const { position } = this.photos[this.selected];
        this.setPosition(position.x + step[0], position.y + step[1]);
      }

      async onPhotoChange(event) {
        const files = [...event.target.files];
        const photos = this.photos;
        const firstAdded = photos.length;
        event.target.value = '';
        this.error.hidden = true;

        // Saving waits until every chosen photo has been shrunk and added.
        this.saveButton.disabled = true;
        for (const file of files) {
          if (photos.length >= this.maxPhotos) {
            this.showError(this.error.dataset.photoLimit);
            break;
          }
          try {
            photos.push({ src: await this.shrink(file), position: { x: 50, y: 50 } });
          } catch (error) {
            this.showError(this.error.dataset.photo);
          }
        }
        this.saveButton.disabled = false;

        // Unless the popup has since been opened for another vehicle.
        if (photos === this.photos) this.renderPhotos(firstAdded);
      }

      // Photos are shrunk in the browser so uploads stay small and fast on mobile data.
      shrink(file) {
        return new Promise((resolve, reject) => {
          const url = URL.createObjectURL(file);
          const image = new Image();
          image.onload = () => {
            const scale = Math.min(1, 1600 / Math.max(image.width, image.height));
            const canvas = document.createElement('canvas');
            canvas.width = Math.round(image.width * scale);
            canvas.height = Math.round(image.height * scale);
            canvas.getContext('2d').drawImage(image, 0, 0, canvas.width, canvas.height);
            URL.revokeObjectURL(url);
            resolve(canvas.toDataURL('image/jpeg', 0.82));
          };
          image.onerror = () => {
            URL.revokeObjectURL(url);
            reject(new Error());
          };
          image.src = url;
        });
      }

      onPhotoCover() {
        this.photos.unshift(...this.photos.splice(this.selected, 1));
        this.renderPhotos(0);
        this.photoFrame.focus();
      }

      // A photo the garage already has is only deleted once the vehicle is saved.
      onPhotoClear() {
        this.photos.splice(this.selected, 1);
        this.renderPhotos(this.selected);
        (this.photos.length > 0 ? this.photoFrame : this.form.elements.photo).focus();
      }

      async onSubmit(event) {
        event.preventDefault();
        // A vehicle needs a photo: the racer list and the ticket picker rely on one.
        if (this.photos.length === 0) {
          this.showError(this.error.dataset.photoRequired);
          this.form.elements.photo.focus();
          return;
        }
        const data = new FormData(this.form);
        const vehicle = {};
        ['nickname', 'year', 'make', 'model', 'colour', 'rego', 'speedClass', 'drivetrain', 'transmission', 'induction', 'fuel', 'cylinders', 'engineCode', 'power', 'notes'].forEach((field) => {
          vehicle[field] = (data.get(field) || '').trim();
        });
        if (this.editing) vehicle.id = this.editing.id;

        const position = (photo) => `${photo.position.x} ${photo.position.y}`;
        // The photos to keep, in their new order; the app removes any that are left out.
        vehicle.photos = this.photos
          .filter((photo) => photo.id)
          .map((photo) => ({ id: photo.id, position: position(photo) }));

        await this.send(async () => {
          const saved = await this.update(`${this.api}/vehicles`, vehicle);
          // The vehicle now exists, so trying again after a failed upload edits it instead of adding it twice.
          this.editing = { id: saved.id };

          // New photos go up one at a time, each to its own place among the others.
          const added = this.photos.filter((photo) => !photo.id);
          for (const [index, photo] of this.photos.entries()) {
            if (photo.id) continue;
            this.saveButton.textContent = this.saveButton.dataset.uploading
              .replace('[number]', added.indexOf(photo) + 1)
              .replace('[total]', added.length);
            const uploaded = await this.update(`${this.api}/vehicles/photos`, {
              id: saved.id,
              photo: photo.src,
              position: position(photo),
              index,
            });
            photo.id = uploaded.photoId;
          }
        });
      }

      // First click asks for confirmation, second click removes.
      async onRemove() {
        if (!this.removeButton.classList.contains('my-garage__remove--confirm')) {
          this.removeButton.classList.add('my-garage__remove--confirm');
          this.removeButton.textContent = this.removeButton.dataset.confirm;
          return;
        }

        await this.send(() => this.update(`${this.api}/vehicles/delete`, { id: this.editing.id }));
      }

      // Runs a save or a removal, then closes the popup.
      async send(steps) {
        this.error.hidden = true;
        this.setBusy(true);
        try {
          await steps();
          this.dialog.close();
        } catch (error) {
          this.showError(error.message || this.error.dataset.save);
          // Bring the popup back if the customer closed it while it was saving.
          if (!this.dialog.open) this.dialog.showModal();
        } finally {
          this.setBusy(false);
        }
      }

      // Each answer from the app is the customer's whole garage, so the cards stay in step with it.
      async update(url, body) {
        const result = await GarageApi.request(url, body);
        this.vehicles = result.vehicles;
        this.render();
        return result;
      }

      setBusy(busy) {
        this.busy = busy;
        this.saveButton.disabled = busy;
        this.removeButton.disabled = busy;
        // Photos cannot change while they upload, and no other vehicle can be opened until this one is saved.
        this.photoArea.inert = busy;
        this.addButton.disabled = busy;
        this.list.classList.toggle('my-garage__grid--busy', busy);
        this.saveButton.textContent = busy ? this.saveButton.dataset.busy : this.saveButton.dataset.label;
      }

      resetRemove() {
        this.removeButton.classList.remove('my-garage__remove--confirm');
        this.removeButton.textContent = this.removeButton.dataset.label;
      }

      showError(message) {
        this.error.textContent = message;
        this.error.hidden = false;
      }
    }
  );
}

if (!customElements.get('garage-picker')) {
  customElements.define(
    'garage-picker',
    class GaragePicker extends HTMLElement {
      async connectedCallback() {
        this.select = this.querySelector('select');
        this.error = this.querySelector('[data-garage-picker-error]');
        if (this.hasAttribute('data-required')) {
          this.onFormSubmit = this.onFormSubmit.bind(this);
          // Capturing on the document runs this before the product form adds to the cart.
          document.addEventListener('submit', this.onFormSubmit, true);
        }
        // Logged-out customers get a log-in hint from Liquid instead of the picker.
        if (!this.select) return;

        this.vehicleInput = this.querySelector('[data-garage-vehicle-input]');
        this.regoInput = this.querySelector('[data-garage-rego-input]');
        this.classInput = this.querySelector('[data-garage-class-input]');
        // One hidden input per saved detail that travels with the ticket as a line item property.
        this.detailInputs = [
          ['drivetrain', this.querySelector('[data-garage-drive-input]')],
          ['induction', this.querySelector('[data-garage-induction-input]')],
          ['fuel', this.querySelector('[data-garage-fuel-input]')],
          ['cylinders', this.querySelector('[data-garage-cylinders-input]')],
          ['transmission', this.querySelector('[data-garage-transmission-input]')],
          ['power', this.querySelector('[data-garage-power-input]')],
          ['engineCode', this.querySelector('[data-garage-engine-input]')],
        ].filter(([, input]) => input);
        this.select.addEventListener('change', this.update.bind(this));

        try {
          this.vehicles = (await GarageApi.request(`${this.dataset.api}/vehicles`)).vehicles;
        } catch (error) {
          // The ticket can still be bought without a saved vehicle, even a required one,
          // so a garage outage never stops ticket sales.
          this.unavailable = true;
          return;
        }

        this.querySelector('[data-garage-picker-empty]').hidden = this.vehicles.length > 0;
        this.querySelector('[data-garage-picker-field]').hidden = this.vehicles.length === 0;

        this.vehicles.forEach((vehicle) => {
          const option = document.createElement('option');
          option.value = vehicle.id;
          option.textContent = [vehicle.nickname, GarageApi.title(vehicle)].filter(Boolean).join(' — ');
          if (!this.hasPhoto(vehicle)) option.textContent += ' (no photo yet)';
          this.select.appendChild(option);
        });

        if (this.vehicles.length === 1) this.select.value = this.vehicles[0].id;
        this.update();
      }

      // Disabled inputs are left off the order, so nothing is sent until a car is chosen.
      update() {
        const vehicle = this.vehicles.find((saved) => saved.id === this.select.value);
        const description = vehicle ? [GarageApi.title(vehicle), vehicle.colour].filter(Boolean).join(', ') : '';

        this.vehicleInput.value = description;
        this.vehicleInput.disabled = !description;
        this.regoInput.value = vehicle ? vehicle.rego : '';
        this.regoInput.disabled = !this.regoInput.value;
        this.classInput.value = (vehicle && vehicle.speedClass) || '';
        this.classInput.disabled = !this.classInput.value;
        this.detailInputs.forEach(([field, input]) => {
          input.value = (vehicle && vehicle[field]) || '';
          input.disabled = !input.value;
        });
        if (this.error && description) this.error.hidden = true;
      }

      disconnectedCallback() {
        if (this.onFormSubmit) document.removeEventListener('submit', this.onFormSubmit, true);
      }

      // Vehicles saved before photos were required may still have none.
      hasPhoto(vehicle) {
        return Boolean(vehicle.photo) || (Array.isArray(vehicle.photos) && vehicle.photos.length > 0);
      }

      // Returns which error message applies, or nothing when the entry can go in the cart.
      missingVehicle() {
        if (!this.select) return 'login';
        if (this.unavailable) return '';
        if (!this.vehicles) return 'loading';
        if (this.vehicles.length === 0) return 'empty';
        if (this.vehicleInput.disabled) return 'choose';
        if (this.classInput.disabled) return 'class';
        const vehicle = this.vehicles.find((saved) => saved.id === this.select.value);
        return vehicle && !this.hasPhoto(vehicle) ? 'photo' : '';
      }

      onFormSubmit(event) {
        // The product form's hidden "id" input shadows form.id, so read the attribute.
        if (event.target.getAttribute('id') !== this.dataset.form) return;

        const missing = this.missingVehicle();
        if (!missing) return;

        event.preventDefault();
        event.stopPropagation();
        this.error.querySelector('[data-garage-picker-error-text]').textContent = this.error.getAttribute(
          `data-message-${missing}`
        );
        this.error.hidden = false;
        // Otherwise the first link on show: log in, or My Garage.
        const visibleLink = [...this.querySelectorAll('a')].find((link) => link.offsetParent);
        const target = missing === 'choose' ? this.select : visibleLink;
        if (target) target.focus();
      }
    }
  );
}
